import { useEffect, type RefObject } from "react";

/**
 * Brings the end of a conversation back into view when something new arrives.
 *
 * Only when it isn't already visible, and only for a comment that lands while
 * the card is open: the thread sits where the reader left it, and moving that
 * text unasked is worse than leaving a new reply below the fold.
 */
export function useScrollToNewest({
  threadId,
  containerRef,
  newestId,
}: {
  threadId: string;
  containerRef: RefObject<HTMLElement | null>;
  /** The last comment in the thread; a change is what counts as new. */
  newestId: string | undefined;
}): void {
  useEffect(() => {
    if (newestId === undefined) {
      return;
    }

    const alreadySeen = seen.get(threadId);
    seen.set(threadId, newestId);

    if (alreadySeen === undefined || alreadySeen === newestId) {
      return;
    }

    const container = containerRef.current;

    if (!container) {
      return;
    }

    // A tolerance, not an exact test: a partly-visible last comment still reads
    // as already there.
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    if (distanceFromBottom <= NEAR_BOTTOM_PX) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [threadId, containerRef, newestId]);
}

/** How close to the end still counts as already looking at it. */
const NEAR_BOTTOM_PX = 24;

/**
 * The last comment each thread was seen with.
 *
 * Module-level because the card remounts when a reply refetches the pin list it
 * is drawn from — the very event this exists for. Seeded per mount, the
 * arriving comment would look like the one already on screen.
 */
const seen = new Map<string, string>();

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}
